import Skeleton from 'react-loading-skeleton';

import { CustomerGroupSettingsMessage } from '../../../../../types/CustomerGroupSettingsMessage';
import { SupplierGroupSettingsMessage } from '../../../../../types/SupplierGroupSettingsMessage';
import { Callout } from '../../../../cdl/Callout/Callout';
import { Switch } from '../../../../cdl/Switch/Switch';
import { Text } from '../../../../cdl/Text/Text';
import { GenericError } from '../../../../common/Error/GenericError';
import { translate } from '../../../../common/helpers/translate.helper';
import { IconInfoCircle } from '../../../../common/icons/cdl/InfoCircle';
import { Box } from '../../../../common/ui/Box';
import { useCustomerGroupSettings } from '../hooks/useCustomerGroupSettings';
import { useCustomerGroupSettingsOptimisticMutation } from '../hooks/useCustomerGroupSettingsOptimisticMutation';
import { useSupplierGroupSettings } from '../hooks/useSupplierGroupSettings';
import { useSupplierGroupSettingsOptimisticMutation } from '../hooks/useSupplierGroupSettingsOptimisticMutation';

interface MfaSettingsProps {
    groupId: string;
}

export const CustomerMfaSettings = ({ groupId }: MfaSettingsProps) => {
    const { data, isPending, isError } = useCustomerGroupSettings(groupId);
    const customerGroupSettingsUpdateMutation = useCustomerGroupSettingsOptimisticMutation(groupId);
    return (
        <MfaSettings
            groupSettings={data}
            isPending={isPending}
            isError={isError}
            onChange={() => data && customerGroupSettingsUpdateMutation.toggleEnforceMfa(data)}
        />
    );
};

export const SupplierMfaSettings = ({ groupId }: MfaSettingsProps) => {
    const { data, isPending, isError } = useSupplierGroupSettings(groupId);
    const supplierGroupSettingsUpdateMutation = useSupplierGroupSettingsOptimisticMutation(groupId);
    return (
        <MfaSettings
            groupSettings={data}
            isPending={isPending}
            isError={isError}
            onChange={() => data && supplierGroupSettingsUpdateMutation.toggleEnforceMfa(data)}
        />
    );
};

type GroupSettings = CustomerGroupSettingsMessage | SupplierGroupSettingsMessage;

interface InternalMfaSettingsProps {
    groupSettings: GroupSettings | undefined;
    isPending: boolean;
    isError: boolean;
    onChange: () => void;
}

const MfaSettings = ({ groupSettings, isPending, isError, onChange }: InternalMfaSettingsProps) => {
    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Text variant="subtitle1" as="h2">
                {translate('customers.twoStepAuthHeadline')}
            </Text>
            <Callout
                variant="accent"
                heading={translate('customers.twoStepAuthSwitchCalloutTitle')}
                Icon={IconInfoCircle}
                description={
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Text>&bull; {translate('customers.twoStepAuthSwitchCalloutLine1')}</Text>
                        <Text>&bull; {translate('customers.twoStepAuthSwitchCalloutLine2')}</Text>
                    </Box>
                }
            />
            {isPending ? <Skeleton height="40px" width="70%" /> : null}
            {isError ? <GenericError /> : null}
            {groupSettings ? (
                <Switch
                    onChange={onChange}
                    checked={groupSettings.enforceMfa}
                    label={translate('customers.twoStepAuthSwitchLabel')}
                />
            ) : null}
        </Box>
    );
};
